import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { produce } from "immer";

import { CustomerTab } from "../CustomerTab/schema";
import { useSession } from "../Session/SessionContext";
import { addCustomerSession } from "./reducer";
import {
  createCustomerSessionForSelf,
  createSession,
  fetchSession,
} from "./requests";

export const billSplitKeys = {
  current: (customerTabUuid: string) => [
    "billSplit",
    customerTabUuid?.toLowerCase(),
  ],
};

export const useBillSplitQuery = (customerTabUuid?: string) => {
  return useQuery({
    queryKey: billSplitKeys.current(customerTabUuid || "undefined"),
    queryFn: () =>
      fetchSession({ customerTabUuid: customerTabUuid || "undefined" }),
    enabled: !!customerTabUuid,
  });
};

export const useLoadedBillSplit = () => {
  const { data, isError, error, isFetching, isPending } = useCurrentBillSplit();
  if (isError) {
    throw `Error loading query, ${error.message}`;
  }
  if (isPending) {
    throw `Attempt to access loaded billSplit session that is  unloaded`;
  }
  if (!data) {
    throw `Attempt to access billSplit session that is  missing`;
  }

  return {
    data,
    isFetching,
  };
};

export const useCurrentBillSplit = () => {
  const { customerTab } = useSession();
  if (!customerTab?.uuid) {
    throw "Missing a customer tab";
  }
  return useBillSplit(customerTab.uuid);
};

export const useBillSplit = (customerTabUuid: string) =>
  useQuery({
    queryKey: billSplitKeys.current(customerTabUuid),
    queryFn: () => fetchSession({ customerTabUuid }),
  });

export const useCreateBillSplitCustomerSession = (customerTab: CustomerTab) => {
  const queryClient = useQueryClient();
  const { data: billSplitSession } = useBillSplit(customerTab.uuid);
  return useMutation({
    mutationFn: async () => {
      if (!billSplitSession) {
        throw "no bill split session";
      }
      const customerSession = await createCustomerSessionForSelf({
        billSplitUuid: billSplitSession.uuid,
      });
      queryClient.setQueryData(
        billSplitKeys.current(customerTab.uuid),
        produce(billSplitSession, (draft) => {
          addCustomerSession(draft, customerSession);
        })
      );
    },
  });
};

export const useCreateBillSplit = (customerTab: CustomerTab) => {
  const queryClient = useQueryClient();
  const uuid = customerTab?.uuid;
  if (!uuid) {
    throw "no uuid";
  }
  return useMutation({
    mutationFn: async () => {
      const billSplitSession = await createSession({
        customerTabUuid: uuid,
      });
      queryClient.setQueryData(
        billSplitKeys.current(customerTab.uuid),
        billSplitSession
      );
    },
  });
};
